import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import {
  isRouteErrorResponse,
  useParams,
  useRouteError,
} from "react-router-dom";

import { FullPageError } from "@oneleet/components";

let globalErrorId: string | undefined;

function isErrorWithMessage(obj: unknown): obj is { message: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "message" in obj &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-member-access
    typeof (obj as any).message === "string"
  );
}

export function ErrorBoundary({
  showLogoutButton,
}: {
  showLogoutButton?: boolean;
}) {
  const { tenant: tenantId } = useParams();
  const error = useRouteError();

  // This will catch when the cached bundle is no longer available and reload the page
  // to fetch the latest version.
  useEffect(() => {
    if (
      isErrorWithMessage(error) &&
      (error.message.includes("Failed to fetch dynamically imported module") ||
        error.message.includes("Importing a module script failed"))
    ) {
      const reloadTimestamp = parseInt(
        sessionStorage.getItem("oneleet_already_reloaded") || "0",
      );

      // Only reload if the reload meta is older than 1 minute
      if (Date.now() - reloadTimestamp > 1000 * 60) {
        sessionStorage.setItem(
          "oneleet_already_reloaded",
          Date.now().toString(),
        );
        window.location.reload();
      }
    }
  }, [error]);

  let errorStatus;
  let errorText;
  let errorDescription;

  const [sentryErrorId, setSentryErrorId] = useState<string | undefined>(
    globalErrorId,
  );

  useEffect(() => {
    if (globalErrorId) return;
    const errorId = captureException(error, {
      level: "fatal",
      tags: {
        ...(tenantId ? { tenantId } : {}),
      },
    });
    setSentryErrorId(errorId);
    globalErrorId = errorId;
  }, [error, tenantId]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      errorStatus = "403";
      errorText = "Not allowed";
      errorDescription =
        "You are not allowed to access this page. Please contact your team administrator.";
    }

    if (error.status === 404) {
      errorStatus = "404";
      errorText = "Not found";
      errorDescription = "Please double check the URL and try again.";
    }
  }

  return (
    <FullPageError
      status={errorStatus}
      title={errorText}
      description={errorDescription}
      showLogoutButton={showLogoutButton}
      sentryErrorId={sentryErrorId}
    />
  );
}
